import React, {
  forwardRef,
  HTMLAttributes,
  MutableRefObject,
  useRef
} from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'

import { cn } from '@/lib/utils'

export interface SymbolProps extends HTMLAttributes<HTMLSpanElement> {
  isAnimated?: boolean
  className?: string
}

const Symbol = forwardRef<HTMLSpanElement, SymbolProps>(
  ({ className, isAnimated, ...props }, ref) => {
    const localRef = useRef<HTMLSpanElement | null>(null)

    const combinedRef = (node: HTMLSpanElement | null) => {
      localRef.current = node

      if (typeof ref === 'function') {
        ref(node)
      } else if (ref) {
        ;(ref as MutableRefObject<HTMLSpanElement | null>).current = node
      }
    }

    useGSAP(
      () => {
        if (!isAnimated || !localRef.current) return

        localRef.current.classList.remove('invisible')

        const firstLine = localRef.current.querySelector(
          '[data-motion-first-line]'
        ) as SVGPathElement
        const secondLine = localRef.current.querySelector(
          '[data-motion-second-line]'
        ) as SVGPathElement
        const otherPaths = localRef.current.querySelectorAll(
          'path:not([data-motion-first-line]):not([data-motion-second-line])'
        )

        if (!firstLine || !secondLine) return

        const tl = gsap.timeline({
          defaults: { ease: 'power2.out', delay: 0.2 }
        })

        tl.fromTo(
          firstLine,
          { drawSVG: '0%' },
          { drawSVG: '100%', duration: 1 }
        )
          .fromTo(
            secondLine,
            { drawSVG: '0%' },
            { drawSVG: '100%', duration: 1 },
            '<'
          )

          .fromTo(
            otherPaths,
            { autoAlpha: 0 },
            { autoAlpha: 1, duration: 0.5, stagger: 0.15 },
            '<0.8'
          )
      },
      {
        dependencies: [isAnimated],
        scope: localRef,
        revertOnUpdate: true
      }
    )

    return (
      <span
        ref={combinedRef}
        className={cn(
          'relative z-10 flex items-center',
          { invisible: isAnimated },
          className
        )}
        {...props}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 12 11.98"
          width="12"
          height="11.98"
          className="h-full w-full fill-current"
          data-motion-symbol
        >
          <path d="M.85 1.61c.4 0 .73-.33.73-.74S1.25.13.85.13.12.46.12.87s.33.74.73.74Zm.28 1.18c0 .41.33.74.73.74s.73-.33.73-.74-.33-.74-.73-.74-.73.33-.73.74Zm1.78-1.18c.4 0 .73-.33.73-.74S3.31.13 2.91.13s-.73.33-.73.74.33.74.73.74Z" />
          <path d="M11.88.11H8.29l1.79 3.65 1.8-3.65z" />
          <path d="M1.85 8.34c-.95 0-1.72.79-1.72 1.75v1.75h3.45v-1.75c0-.97-.78-1.75-1.72-1.75Z" />
          <path d="M11.28 9.32h-.82v-.98h-.6v.98h-.82v-.98h-.6v3.51h3.44V8.34h-.6v.98z" />
          <path
            d="M0 5.99h12"
            data-motion-first-line
            className="stroke-current"
            strokeWidth={0.76}
          />
          <path
            d="M6 11.99v-12"
            data-motion-second-line
            strokeWidth={0.76}
            className="stroke-current"
          />
        </svg>
      </span>
    )
  }
)

Symbol.displayName = 'Symbol'

export default Symbol
